'use client';
import { IC_EXPRESS, IC_REGULAR } from '@/assets/icons';
import { IM_PRODUCT_DEFAULT } from '@/assets/images';
import Tooltip from '@/components/atoms/Tooltip/Tooltip';
import WishlistToggle from '@/components/atoms/WishlistToggle';
import useProduct from '@/contexts/ProductProvider';
import AroggaAPI from '@/services/ecommerce/apis/AroggaAPI';
import { API_PUBLIC } from '@/services/ecommerce/apis/apiConstrant';
import { generateProductTitle } from '@/services/ecommerce/utils/MetaGenerator';
import { checkDeliveryAndStock } from '@/services/ecommerce/utils/helper';
import { get } from 'lodash';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { CiPlay1 } from 'react-icons/ci';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import ReactPlayer from 'react-player/youtube';
import { Controller, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperRef, SwiperSlide } from 'swiper/react';
import ProductImageModal from '../ProductImageModal';
import styles from './brand-slider.module.scss';
interface Props {
	product: any;
}

export const RenderVideo = ({ yt_key, title, onPlay, onPause, playToggle, playerRef }: any) => {
	useEffect(() => {
		const player = playerRef.current?.getInternalPlayer();

		if (player) {
			if (playToggle) {
				player.playVideo();
			} else {
				player.pauseVideo();
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [playToggle]);

	useEffect(() => {
		// Seek to the beginning when the component mounts
		if (playerRef.current) {
			playerRef.current.seekTo(0, 'seconds');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className={styles.slider_video_product}>
			<ReactPlayer
				width={'100%'}
				height={'100%'}
				url={`https://www.youtube.com/watch?v=${yt_key}`}
				controls={true} // Enable native YouTube controls
				ref={playerRef}
				volume={1}
				playing={false}
			/>
		</div>
	);
};

const ProductImageSlider: React.FC<Props> = ({ product }) => {
	const { selectedVariant, globalData, setGlobalData } = useProduct();
	const [activeBorder, setActiveBorder] = useState<any>(false);
	const [playToggle, setPlayToggle] = useState(false);
	const search = useSearchParams();
	const pvId = search.get('pv_id');
	const [swiper, setSwiper] = useState<any>();
	const [thumbsSwiper, setThumbsSwiper] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const playerRef = useRef<any>(null);

	////////////////////////////////////////

	const prevSelectedVariantRef = useRef<any>({});

	const getGlobalData = async (selectedVariantId) => {
		try {
			const { data } = await AroggaAPI.post(API_PUBLIC.CHECK_WISHLIST(selectedVariantId));
			setGlobalData(data);
		} catch (error) {
			console.error('Error fetching global data:', error);
		}
	};

	useEffect(() => {
		// Check if selectedVariant has changed
		if (selectedVariant?.pv_id && prevSelectedVariantRef.current?.pv_id !== selectedVariant.pv_id) {
			getGlobalData(selectedVariant.pv_id);
			prevSelectedVariantRef.current = selectedVariant;
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedVariant]);

	///////////////////////////////////

	const generateImageArrayUrls = () => {
		const imagesArray = [];

		if (selectedVariant?.attachedFiles_pv_images?.length > 0) {
			imagesArray.push(...selectedVariant.attachedFiles_pv_images);
		}

		if (product?.p_images?.length > 0) {
			imagesArray.push(...product.p_images);
		}

		if (product?.attachedFiles_p_images?.length > 0) {
			imagesArray.push(...product.attachedFiles_p_images);
		}

		return imagesArray;
	};

	const productImages = generateImageArrayUrls();
	const product_type = get(product, 'p_type', 'medicine');
	const type = product_type === 'pet_&_vet' ? 'pet_and_vet' : product_type;

	const data =
		productImages?.length > 0
			? productImages
			: [
					{
						// src: getProductImage(product),
						src: get(product, 'attachedFiles_p_images[0].src', IM_PRODUCT_DEFAULT[type]),
						title: product?.p_name
					}
				];

	const totalSlides = data.length + (product?.p_yt_key && product?.p_yt_title ? 1 : 0); // Calculate total slides

	const swiperRef = useRef<SwiperRef | null>(null);
	const [allowSlide, setAllowSlide] = useState({
		allowSlidePrev: false,
		allowSlideNext: true
	});
	const { stock, isExpress, delevery, description, short_d_text } = checkDeliveryAndStock(product, selectedVariant);

	const handleVideoPlay = () => {
		if (thumbsSwiper) {
			thumbsSwiper.autoplay.stop();
		}
	};

	const handleVideoPause = () => {
		if (thumbsSwiper) {
			thumbsSwiper.autoplay.start();
		}
	};

	useEffect(() => {
		setActiveBorder(false);
		if (swiper && data) {
			const player = playerRef.current?.getInternalPlayer();
			if (playToggle && player?.seekTo) {
				player?.seekTo && player?.seekTo(0);
			}
			setPlayToggle(false);
			setActiveBorder(false);
			swiper.slideTo(0, 0);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pvId]);

	return (
		<div>
			<ProductImageModal
				images={data}
				isOpen={isOpen}
				onClose={() => setIsOpen(false)}
				onConfirm={() => setIsOpen(false)}
			/>
			<div
				className='border1-grey300'
				style={{
					display: 'flex',
					flexDirection: 'row',
					backgroundColor: 'white',
					borderRadius: '10px',
					height: '660px',
					gap: '10px',
					padding: '20px',
					marginBottom: '20px',
					position: 'relative',
					overflow: 'hidden' // Ensure children don't overflow
				}}>
				<Swiper
					className='thumbSlider'
					modules={[Pagination, Controller, Thumbs]}
					onSwiper={(swiper) => setThumbsSwiper(swiper)}
					watchSlidesProgress
					style={{
						width: '100px',
						height: '100%',
						position: 'relative',
						overflow: 'hidden'
					}}
					slidesPerView={8}
					direction='vertical'>
					{allowSlide.allowSlidePrev && (
						<div
							className={styles.prev}
							onClick={() => {
								const swiperInstance = swiperRef.current?.swiper;
								swiperInstance?.slidePrev();
							}}>
							<IoIosArrowUp />
						</div>
					)}
					{data?.map((d: any, i: number) => (
						<SwiperSlide key={i}>
							<Image
								src={d?.src}
								className='border1-grey300'
								style={{
									width: '70px',
									height: '70px',
									borderRadius: '6px',
									cursor: 'pointer',
									objectFit: 'contain'
								}}
								unoptimized
								alt={generateProductTitle(product)}
								width={50}
								height={50}
								onClick={() => {
									setActiveBorder(false);
								}}
							/>
						</SwiperSlide>
					))}
					{product?.p_yt_key && product?.p_yt_title && (
						<SwiperSlide>
							<CiPlay1
								className='border-grey300'
								style={{
									width: '70px',
									height: '70px',
									border: activeBorder ? '1px solid #0e7673' : '1px solid #eeeff2',
									borderRadius: '6px',
									cursor: 'pointer',
									objectFit: 'contain'
								}}
								onClick={() => {
									setActiveBorder(true);
								}}
							/>
						</SwiperSlide>
					)}
					{data.length > 8 && allowSlide.allowSlideNext && (
						<div
							className={styles.next}
							onClick={() => {
								const swiperInstance = swiperRef.current?.swiper;
								swiperInstance?.slideNext();
							}}>
							<IoIosArrowDown />
						</div>
					)}
				</Swiper>

				<Swiper
					ref={swiperRef}
					style={{
						maxWidth: '580px',
						width: '100%',
						height: '100%',
						justifyContent: 'center',
						alignItems: 'center',
						position: 'relative',
						overflow: 'hidden'
					}}
					onSlideChange={(e) => {
						setAllowSlide({
							allowSlidePrev: !e.isBeginning,
							allowSlideNext: !e.isEnd
						});
						if (product?.p_yt_key) {
							if (e.activeIndex === totalSlides - 1) {
								setPlayToggle(true);
								setActiveBorder(true);
							} else {
								setPlayToggle(false);
								setActiveBorder(false);
							}
						}
					}}
					modules={[Thumbs]}
					thumbs={{ swiper: thumbsSwiper }}
					slidesPerView={1}
					spaceBetween={20}
					autoplay={{
						delay: 2500,
						disableOnInteraction: false,
						pauseOnMouseEnter: true
					}}
					onSwiper={(swiper) => setSwiper(swiper)}
					pagination={false}>
					{data?.map((d, i) => (
						<SwiperSlide
							key={i}
							onClick={() => {
								setIsOpen(true);
							}}
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center'
							}}>
							<div className={styles.brand_slider}>
								<ReactImageMagnifys
									enlargedImagePortalId='enlarged-portal'
									{...{
										smallImage: {
											alt: generateProductTitle(product),
											isFluidWidth: true,
											src: d?.src,
											sizes: '(max-width: 500px) 100vw, (max-width: 1200px) 30vw, 360px',
											aspectRatio: 1,
											objectFit: 'contain'
										},
										largeImage: {
											src: d?.src,
											alt: generateProductTitle(product),
											width: 1600,
											height: 2000
										},
										enlargedImageContainerDimensions: {
											width: '100%',
											height: '100%',
											marginRight: '100px'
										},
										enlargedImageContainerStyle: {
											zIndex: '1500',
											height: '100px',
											width: '100px',
											backgroundColor: 'white',
											borderRadius: '10px',
											border: '1px solid #ccc'
										}
									}}
								/>
							</div>
						</SwiperSlide>
					))}
					{product?.p_yt_key && product?.p_yt_title && (
						<SwiperSlide>
							<RenderVideo
								yt_key={product?.p_yt_key}
								title={product?.p_yt_title}
								playToggle={playToggle}
								onPlay={handleVideoPlay}
								onPause={handleVideoPause}
								setActiveBorder={setActiveBorder}
								playerRef={playerRef}
							/>
							<div
								onClick={() => setPlayToggle(!playToggle)}
								style={{
									content: '',
									background: 'transparent',
									width: '100%',
									height: '50%',
									minHeight: '1px',
									position: 'absolute',
									opacity: '0.5',
									top: '20%',
									zIndex: 999
								}}
							/>
						</SwiperSlide>
					)}
					{stock ? (
						<div className={styles.expressicon}>
							<Tooltip
								placement='top'
								style={{
									backgroundColor: 'white',
									color: 'black',
									fontSize: '12px',
									padding: '10px',
									borderRadius: '10px',
									border: '1px solid #ccc',
									boxShadow: '0px 0px 10px 0px #ccc'
								}}
								content={
									<>
										<div className='fw-700'>{delevery}</div>
										<div
											style={{
												fontSize: '12px',
												marginTop: '5px'
											}}>
											{description}
										</div>
									</>
								}>
								<span>
									{!isExpress ? <IC_REGULAR /> : <IC_EXPRESS />}
									<span>{short_d_text}</span>
								</span>
							</Tooltip>
						</div>
					) : (
						<div className={styles.outofstockicon}>
							<span
								style={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'center'
								}}>
								Out of stock
							</span>
						</div>
					)}
					<div className={styles.shareicon}>
						{selectedVariant && <WishlistToggle variant={selectedVariant} globalData={globalData} />}
					</div>
				</Swiper>
			</div>
		</div>
	);
};
export default ProductImageSlider;

const ReactImageMagnifys = ({
	smallImage,
	largeImage,
	enlargedImagePortalId,
	enlargedImageContainerStyle,
	enlargedImageContainerDimensions
}) => {
	const [isHovering, setIsHovering] = useState(false);
	const [position, setPosition] = useState({ x: 0, y: 0 });
	useEffect(() => {
		const portalElem = document.getElementById(enlargedImagePortalId);
		if (!portalElem) {
			console.error(`Element with ID '${enlargedImagePortalId}' not found.`);
		}
	}, [enlargedImagePortalId]);

	const handleMouseMove = (e) => {
		const { left, top, width, height } = e.target.getBoundingClientRect();
		const x = ((e.pageX - left) / width) * 100;
		const y = ((e.pageY - top) / height) * 100;
		setPosition({ x, y });
	};
	const containerStyle = {
		position: 'relative',
		cursor: isHovering ? 'crosshair' : 'default' // Cursor as '+' when hovering
	};

	const magnifiedImage = (
		<div
			style={{
				position: 'absolute',
				left: 0,
				top: 0,
				backgroundImage: `url(${largeImage.src})`,
				backgroundRepeat: 'no-repeat',
				backgroundPosition: `${position.x}% ${position.y}%`,
				backgroundSize: `${largeImage.width}px ${largeImage.height}px`,
				width: '100%',
				height: '660px',
				zIndex: 1500,
				// ...enlargedImageContainerDimensions,
				backgroundColor: 'white',
				border: '1px solid #ccc',
				borderRadius: '10px'
			}}
		/>
	);
	const lensStyle = {
		position: 'absolute',
		left: `${position.x}%`,
		top: `${position.y}%`,
		transform: 'translate(-50%, -50%)',
		...enlargedImageContainerStyle,
		backgroundImage: `url(${smallImage.src})`,
		backgroundRepeat: 'no-repeat',
		backgroundPosition: `${position.x}% ${position.y}%`,
		backgroundSize: `${smallImage.width}px ${smallImage.height}px`,
		pointerEvents: 'none',
		zIndex: 1500
	};
	const backdropStyle = {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(0, 0, 0, 0.3)',
		zIndex: 1000,
		opacity: isHovering ? 1 : 0,
		transition: 'opacity 800ms ease-in-out'
	};

	return (
		<div
			onMouseEnter={() => setIsHovering(true)}
			onMouseLeave={() => setIsHovering(false)}
			onMouseMove={handleMouseMove}
			style={containerStyle as any}>
			{/* @next/next/no-img-element */}

			<Image
				unoptimized
				src={smallImage.src}
				alt={smallImage.alt}
				width={100}
				height={100}
				style={{
					backgroundColor: 'white'
				}}
			/>
			<div style={backdropStyle as any} />
			{isHovering && <div style={lensStyle} />}
			{isHovering && ReactDOM.createPortal(magnifiedImage as any, document.getElementById(enlargedImagePortalId))}
		</div>
	);
};
